//------------------MAIN COMPONENT--------------------
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Col, Row, Form, Button, Alert, Modal, ProgressBar } from "react-bootstrap";
import Select from "react-select";
import { useRegisterRcsTemplateMutation } from "Services/rcsServices";
import RcsCarouselCardForm from "./RcsCarouselCardForm";
import RcsDropMediaAndPreview from "./RcsDropMediaAndPreview";
import RcsSuggestionsField from "./RcsSuggestionsField";
import { useHistory } from "react-router-dom";
import { Routes } from "routes";
import RcsTemplatePreview from "./RcsTemplatePreview";
import { FcPlus } from "react-icons/fc";
import moment from "moment";
import { RCS_PROVIDERS } from "utils/constant";
import { CheckCircleIcon, ClockIcon, ShieldExclamationIcon } from "@heroicons/react/solid";

const { KARIX, SMART_PING } = RCS_PROVIDERS;
const provider = KARIX;

export const templateTypeOptions = [
  { value: "text_message", label: "Text Message" },
  { value: "rich_card", label: "Rich Card" },
  { value: "carousel", label: "Carousel" },
];

export const cardOrientationOptions = [
  { value: "HORIZONTAL", label: "Horizontal" },
  { value: "VERTICAL", label: "Vertical" },
];

export const heightOptions = [
  { value: "SHORT", label: "Short" },
  { value: "MEDIUM", label: "Medium" },
];

export const alignmentOptionsMap = {
  HORIZONTAL: [
    { value: "LEFT", label: "Left" },
    { value: "RIGHT", label: "Right" },
  ],
  VERTICAL: [
    { value: "TOP", label: "Top" },
    { value: "BOTTOM", label: "Bottom" },
  ],
};

const templateCategoryOptions = [
  { value: "TRANSACTIONAL", label: "Transactional" },
  { value: "PROMOTIONAL", label: "Promotional" },
  { value: "OTP", label: "OTP" },
];

const getNextVariableName = (text, prefix = "custom_param_desc_") => {
  const regex = new RegExp(`\\[${prefix}(\\d+)\\]`, "g");
  let max = 0;
  let match;
  while ((match = regex.exec(text))) {
    const n = parseInt(match[1], 10);
    if (n > max) max = n;
  }
  return `[${prefix}${max + 1}]`;
};

const insertAtCursor = (ref, value, setValue, insertText) => {
  if (!ref.current) {
    setValue((prev) => prev + insertText);
    return;
  }
  const input = ref.current;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  const newValue = value.slice(0, start) + insertText + value.slice(end);
  setValue(newValue);
  setTimeout(() => {
    input.focus();
    input.setSelectionRange(
      start + insertText.length,
      start + insertText.length
    );
  }, 0);
};

const formatDateTimeToYMDHMS = (dateStr) => {
  if (!dateStr) return "";
  const m = moment(dateStr);
  if (!m.isValid()) return dateStr;
  return m.format("YYYY-MM-DD HH:mm:ss");
};

const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) return reject("No file");
    const img = new window.Image();
    img.onload = function () {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = function () {
      reject("Failed to load image");
    };
    img.src = URL.createObjectURL(file);
  });
};

const getVideoDimensions = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) return reject("No file");
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = function () {
      resolve({ width: video.videoWidth, height: video.videoHeight });
      URL.revokeObjectURL(video.src);
    };
    video.onerror = function () {
      reject("Failed to load video");
    };
    video.src = URL.createObjectURL(file);
  });
};

const getSmartPingThumbnailDimensions = (
  context,
  orientation,
  height,
  width
) => {
  if (context === "rich_card") {
    if (orientation === "VERTICAL") {
      if (height === "MEDIUM") {
        return { width: 920, height: 420 };
      }
      return { width: 1440, height: 480 };
    }
    if (orientation === "HORIZONTAL") {
      return { width: 1000, height: 1320 };
    }
  }
  if (context === "carousel") {
    if (height && width) {
      const dims = getRequiredDimensions(
        "VERTICAL",
        height,
        "carousel",
        width,
        "IMAGE"
      );
      if (dims && dims.width && dims.height) {
        return { width: dims.width, height: dims.height };
      }
    }
    return { width: 1440, height: 720 };
  }
  return { width: 1440, height: 480 };
};

const getRequiredDimensions = (
  orientation,
  height,
  context = "rich_card",
  width = null,
  type,
  alignment = null
) => {
  const KARIX_RICH_CARD = {
    VIDEO: {
      VERTICAL: {
        SHORT: {
          width: 770,
          // height: 257, In doc, unable to convert video to odd pixels from any online resizer
          height: 256,
          aspectRatio: 3 / 1,
          aspectRatioLabel: "3:1",
          maxSizeMB: 10,
        },
        MEDIUM: {
          width: 770,
          // height: 335,
          height: 336,
          aspectRatio: 7 / 3,
          aspectRatioLabel: "7:3",
          maxSizeMB: 10,
        },
      },
      HORIZONTAL: {
        width: 250,
        height: 330,
        aspectRatio: 25 / 33,
        aspectRatioLabel: "25:33",
        maxSizeMB: 10,
      },
    },
    IMAGE: {
      HORIZONTAL: {
        width: 768,
        height: 1024,
        aspectRatio: 3 / 4,
        aspectRatioLabel: "3:4",
        maxSizeMB: 2,
      },
      VERTICAL: {
        SHORT: {
          width: 1440,
          height: 480,
          aspectRatio: 3 / 1,
          aspectRatioLabel: "3:1",
          maxSizeMB: 2,
        },
        MEDIUM: {
          width: 1440,
          height: 720,
          aspectRatio: 2 / 1,
          aspectRatioLabel: "2:1",
          maxSizeMB: 2,
        },
      },
    },
  };

  const KARIX_CAROUSEL = {
    VIDEO: {
      SMALL: {
        SHORT: {
          // width: 565,
          width: 564,
          height: 452,
          aspectRatio: 5 / 4,
          aspectRatioLabel: "5:4",
          maxSizeMB: 5,
        },
        MEDIUM: {
          width: 362,
          height: 452,
          aspectRatio: 4 / 5,
          aspectRatioLabel: "4:5",
          maxSizeMB: 5,
        },
      },
      MEDIUM: {
        SHORT: {
          width: 904,
          height: 452,
          aspectRatio: 2 / 1,
          aspectRatioLabel: "2:1",
          maxSizeMB: 5,
        },
        MEDIUM: {
          // width: 605,
          width: 604,
          height: 452,
          aspectRatio: 4 / 3,
          aspectRatioLabel: "4:3",
          maxSizeMB: 5,
        },
      },
    },
    IMAGE: {
      SMALL: {
        SHORT: {
          width: 1160,
          height: 720,
          aspectRatio: 8 / 5,
          aspectRatioLabel: "8:5",
          maxSizeMB: 1,
        },
        MEDIUM: {
          width: 770,
          height: 720,
          aspectRatio: 1,
          aspectRatioLabel: "1:1",
          maxSizeMB: 1,
        },
      },
      MEDIUM: {
        SHORT: {
          width: 1800,
          height: 720,
          aspectRatio: 5 / 2,
          aspectRatioLabel: "5:2",
          maxSizeMB: 1,
        },
        MEDIUM: {
          width: 1280,
          height: 720,
          aspectRatio: 16 / 9,
          aspectRatioLabel: "16:9",
          maxSizeMB: 1,
        },
      },
    },
  };

  const SMART_PING_RICH_CARD = {
    VERTICAL: {
      SHORT: { width: 1440, height: 480, maxSizeMB: type === "IMAGE" ? 1 : 5 },
      MEDIUM: { width: 1440, height: 720, maxSizeMB: type === "IMAGE" ? 1 : 5 },
    },
    HORIZONTAL: {
      width: 768,
      height: 1024,
      maxSizeMB: type === "IMAGE" ? 1 : 5,
    },
  };

  const SMART_PING_CAROUSEL = {
    SHORT: {
      SMALL: { width: 960, height: 768, maxSizeMB: type === "IMAGE" ? 1 : 5 },
      MEDIUM: { width: 1440, height: 720, maxSizeMB: type === "IMAGE" ? 1 : 5 },
    },
    MEDIUM: {
      SMALL: { width: 576, height: 720, maxSizeMB: type === "IMAGE" ? 1 : 5 },
      MEDIUM: {
        width: 1440,
        height: 1080,
        maxSizeMB: type === "IMAGE" ? 1 : 5,
      },
    },
  };

  if (context === "rich_card") {
    if (provider === KARIX) {
      if (KARIX_RICH_CARD[type]) {
        if (KARIX_RICH_CARD[type][orientation]) {
          if (
            typeof KARIX_RICH_CARD[type][orientation] === "object" &&
            height
          ) {
            return KARIX_RICH_CARD[type][orientation][height] || null;
          }
          return KARIX_RICH_CARD[type][orientation] || null;
        }
      }
    } else if (provider === SMART_PING) {
      if (
        orientation === "VERTICAL" &&
        height &&
        SMART_PING_RICH_CARD[orientation][height]
      ) {
        return SMART_PING_RICH_CARD[orientation][height];
      }
      if (orientation === "HORIZONTAL") {
        return SMART_PING_RICH_CARD[orientation];
      }
    }
    return null;
  }

  if (context === "carousel") {
    if (provider === KARIX) {
      if (KARIX_CAROUSEL[type] && width && height) {
        if (
          KARIX_CAROUSEL[type][width] &&
          KARIX_CAROUSEL[type][width][height]
        ) {
          return KARIX_CAROUSEL[type][width][height];
        }
      }
    } else if (provider === SMART_PING) {
      if (
        height &&
        width &&
        SMART_PING_CAROUSEL[height] &&
        SMART_PING_CAROUSEL[height][width]
      ) {
        return SMART_PING_CAROUSEL[height][width];
      }
    }
    return null;
  }

  return null;
};

function aspectRatioEquals(a, b, tolerance = 0.1) {
  if (typeof a === "number" && typeof b === "number") {
    if (Number.isInteger(a) && Number.isInteger(b)) {
      return a === b;
    }
    const aFixed = Number(a.toFixed(2));
    const bFixed = Number(b.toFixed(2));
    let diff;
    if (aFixed > bFixed) {
      diff = Math.abs(aFixed - bFixed);
    } else {
      diff = Math.abs(bFixed - aFixed);
    }
    return diff.toFixed(2) <= tolerance;
  }
  return false;
}

const validateThumbnailFile = async ({
  file,
  type,
  orientation,
  height,
  setError,
  context = "rich_card",
  width = null,
  alignment = null,
  providerOverride = null,
}) => {
  if (!file) return true;

  const prov = providerOverride || provider;

  if (prov === SMART_PING) {
    const maxSizeBytes = 100 * 1024;
    if (file.size > maxSizeBytes) {
      setError("Thumbnail size must be less than 100 KB.");
      return false;
    }
    let dims;
    try {
      dims = await getImageDimensions(file);
      if (!dims) throw new Error("Could not get image dimensions");
    } catch {
      setError("Failed to read thumbnail image dimensions.");
      return false;
    }
    let expectedDims = getSmartPingThumbnailDimensions(
      context,
      orientation,
      height,
      width
    );
    if (
      typeof expectedDims.width === "number" &&
      typeof expectedDims.height === "number" &&
      (dims.width !== expectedDims.width || dims.height !== expectedDims.height)
    ) {
      setError(
        `For SMART_PING, required thumbnail dimensions are ${expectedDims.width}x${expectedDims.height}px. Uploaded file is ${dims.width}x${dims.height}px.`
      );
      return false;
    }
    setError("");
    return true;
  }

  let required;
  let aspectRatioLabel;
  let requiredAspect;
  let tolerance = 0.1;
  if (
    prov === KARIX
  ) {
    // Use VIDEO dimensions for thumbnail as they are same for KARIX
    required = getRequiredDimensions(
      orientation,
      height,
      context,
      width,
      "VIDEO",
      alignment
    );
    aspectRatioLabel = required?.aspectRatioLabel;
    requiredAspect = required?.aspectRatio;
  } else {
    required = getRequiredDimensions(
      orientation,
      height,
      context,
      width,
      "IMAGE",
      alignment
    );
    aspectRatioLabel = required?.aspectRatioLabel;
    requiredAspect = required?.aspectRatio;
  }

  if (!required) {
    setError(
      context === "carousel"
        ? "Please select card height and width before uploading thumbnail."
        : "Please select card orientation and height before uploading thumbnail."
    );
    return false;
  }
  const maxSizeMB = required.maxSizeMB ?? 2;
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    setError(`Thumbnail size must be less than ${maxSizeMB} MB.`);
    return false;
  }
  let dims;
  try {
    dims = await getImageDimensions(file);
    if (!dims) throw new Error("Could not get image dimensions");
  } catch {
    setError("Failed to read thumbnail image dimensions.");
    return false;
  }

  // For KARIX VIDEO thumbnail, check aspect ratio as well
  if (
    prov === KARIX
  ) {
    if (
      typeof required.width === "number" &&
      typeof required.height === "number" &&
      (dims.width !== required.width || dims.height !== required.height)
    ) {
      setError(
        `For thumbnail, required dimensions must be ${required.width}x${required.height}px. Uploaded file is ${dims.width}x${dims.height}px.`
      );
      return false;
    }
    if (requiredAspect) {
      const aspectRatio = dims.width / dims.height;
      if (!aspectRatioEquals(aspectRatio, requiredAspect, tolerance)) {
        setError(
          `For thumbnail, aspect ratio must be ${aspectRatioLabel}. Uploaded file aspect ratio is ${aspectRatio.toFixed(
            2
          )}. Optimal dimensions are ${required.width}x${required.height}px.`
        );
        return false;
      }
    }
    setError("");
    return true;
  }

  if (
    typeof required.width === "number" &&
    typeof required.height === "number" &&
    (dims.width !== required.width || dims.height !== required.height)
  ) {
    setError(
      `For thumbnail, required dimensions must be ${required.width}x${required.height}px. Uploaded file is ${dims.width}x${dims.height}px.`
    );
    return false;
  }
  setError("");
  return true;
};

const validateMediaFile = async ({
  file,
  type,
  orientation,
  height,
  setError,
  context = "rich_card",
  width = null,
  alignment = null,
}) => {
  if (!file) return true;

  const required = getRequiredDimensions(
    orientation,
    height,
    context,
    width,
    type,
    alignment
  );

  if (!required) {
    setError(
      context === "carousel"
        ? "Please select card height and width before uploading media."
        : "Please select card orientation and height before uploading media."
    );
    return false;
  }

  const getMediaLabel = () => (type === "IMAGE" ? "Image" : "Video");
  const getContextLabel = () =>
    context === "carousel" ? "carousel card" : "rich card";

  const maxSizeMB = required.maxSizeMB ?? (type === "IMAGE" ? 1 : 5);
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    setError(`${getMediaLabel()} size must be less than ${maxSizeMB} MB.`);
    return false;
  }

  let dims;
  try {
    dims =
      type === "IMAGE"
        ? await getImageDimensions(file)
        : await getVideoDimensions(file);
    if (!dims) throw new Error("Could not get media dimensions");
  } catch {
    setError(`Failed to read ${getMediaLabel().toLowerCase()} dimensions.`);
    return false;
  }

  if (provider === KARIX && (type === "IMAGE" || type === "VIDEO")) {
    const karixMaxSizeMB = required.maxSizeMB ?? (type === "IMAGE" ? 2 : 10);
    if (file.size > karixMaxSizeMB * 1024 * 1024) {
      setError(
        `${getMediaLabel()} size must be less than ${karixMaxSizeMB} MB.`
      );
      return false;
    }

    const aspectRatio = dims.width / dims.height;
    const requiredAspect = required.aspectRatio;
    const tolerance = 0.1;

    if (
      typeof required.width === "number" &&
      typeof required.height === "number" &&
      (dims.width !== required.width || dims.height !== required.height)
    ) {
      setError(
        `For ${getContextLabel()} ${getMediaLabel().toLowerCase()}, required dimensions must be ${required.width
        }x${required.height}px. Uploaded file is ${dims.width}x${dims.height
        }px.`
      );
      return false;
    }

    if (requiredAspect && !aspectRatioEquals(aspectRatio, requiredAspect, tolerance)) {
      setError(
        `For ${getContextLabel()} ${getMediaLabel().toLowerCase()}, aspect ratio must be ${required.aspectRatioLabel
        }. Uploaded file aspect ratio is ${aspectRatio.toFixed(
          2
        )}. Optimal dimensions are ${required.width}x${required.height}px.`
      );
      return false;
    }

    setError("");
    return true;
  }

  if (
    typeof required.width === "number" &&
    typeof required.height === "number" &&
    (dims.width !== required.width || dims.height !== required.height)
  ) {
    setError(
      `For ${getContextLabel()} ${getMediaLabel().toLowerCase()}, required dimensions must be ${required.width
      }x${required.height}px. Uploaded file is ${dims.width}x${dims.height}px.`
    );
    return false;
  }

  setError("");
  return true;
};

const extractVariablesFromText = (text) => {
  if (!text) return [];
  const regex = /\[(custom_param_[a-zA-Z0-9_]+_\d+)\]/g;
  const found = [];
  let match;
  while ((match = regex.exec(text))) {
    found.push(match[1]);
  }
  return found;
};

const extractVariablesFromCarouselCards = (cards) => {
  const vars = [];
  cards.forEach((card) => {
    vars.push(...extractVariablesFromText(card.title));
    vars.push(...extractVariablesFromText(card.description));
  });
  return vars;
};

const validateTemplateName = (name) => {
  if (!name || typeof name !== "string") return "Template name is required.";
  if (name.length > 20) return "Template name must be at most 20 characters.";
  if (!/^[a-zA-Z0-9_-]+$/.test(name))
    return "Template name can only contain letters, numbers, underscores, and hyphens.";
  return "";
};

export const RcsTemplateComponent = () => {
  const [showModal, setShowModal] = useState(false);
  const [progress, setProgress] = useState(0);
  const [reason, setReason] = useState('');
  const [variant, setVariant] = useState('primary');
  const history = useHistory();
  const [templateName, setTemplateName] = useState("");
  const [templateNameError, setTemplateNameError] = useState("");
  const [templateType, setTemplateType] = useState("text_message");
  const [textMessage, setTextMessage] = useState("");
  const [suggestionInput, setSuggestionInput] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [editingSuggestionIndex, setEditingSuggestionIndex] = useState(null);
  const [registerRCSTemplate] = useRegisterRcsTemplateMutation();

  const [cardOrientation, setCardOrientation] = useState("");
  const [alignmentOptions, setAlignmentOptions] = useState([]);
  const [alignment, setAlignment] = useState("");
  const [height, setHeight] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [richCardMediaType, setRichCardMediaType] = useState("IMAGE");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaFileError, setMediaFileError] = useState("");
  const [richCardThumbnailFile, setRichCardThumbnailFile] = useState(null);
  const [richCardThumbnailError, setRichCardThumbnailError] = useState("");

  const [carouselCards, setCarouselCards] = useState([]);
  const [carouselCardFiles, setCarouselCardFiles] = useState({});
  const [carouselCardThumbnails, setCarouselCardThumbnails] = useState({});
  const [carouselCardMediaErrors, setCarouselCardMediaErrors] = useState({});
  const [carouselCardThumbnailErrors, setCarouselCardThumbnailErrors] =
    useState({});
  const [activeCarouselCardIndex, setActiveCarouselCardIndex] = useState(null);
  const [carouselCardSuggestionInput, setCarouselCardSuggestionInput] =
    useState({});
  const [
    carouselCardSuggestionEditingIndex,
    setCarouselCardSuggestionEditingIndex,
  ] = useState(null);
  const [carouselCardHeight, setCarouselCardHeight] = useState("");
  const [carouselCardWidth, setCarouselCardWidth] = useState("");

  const textMessageRef = useRef(null);
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const [submitTouched, setSubmitTouched] = useState(false);
  const [templateCategory, setTemplateCategory] = useState("TRANSACTIONAL");

  useEffect(() => {
    if (submitTouched || templateName.length > 0) {
      setTemplateNameError(validateTemplateName(templateName));
    } else {
      setTemplateNameError("");
    }
  }, [templateName, submitTouched]);

  const hasValidationError = useMemo(() => {
    if (!templateName.trim() || !!validateTemplateName(templateName)) return true;

    if (!templateType) return true;

    if (templateType === "text_message" || templateType === "standard") {
      if (!textMessage.trim()) return true;
    }

    if (templateType === "rich_card") {
      if (!title.trim()) return true;
      if (!description.trim()) return true;
      if (!cardOrientation) return true;
      if (cardOrientation === "VERTICAL" && !height) return true;
      if (cardOrientation === "HORIZONTAL" && !alignment) return true;
      if (!mediaFile) return true;
      if (mediaFileError) return true;
      if (richCardMediaType === "VIDEO" && !richCardThumbnailFile) return true;
      if (richCardMediaType === "VIDEO" && richCardThumbnailError) return true;
    }

    if (templateType === "carousel") {
      if (!carouselCardHeight) return true;
      if (!carouselCardWidth) return true;
      if (carouselCards.length < 2) return true;
      for (let i = 0; i < carouselCards.length; i++) {
        const card = carouselCards[i];
        if (!card.title.trim()) return true;
        if (!card.description.trim()) return true;
        if (!carouselCardFiles[i]) return true;
        if (carouselCardMediaErrors[i]) return true;
        if (card.mediaType === "VIDEO" && !carouselCardThumbnails[i])
          return true;
        if (card.mediaType === "VIDEO" && carouselCardThumbnailErrors[i])
          return true;
      }
    }

    if (provider === KARIX && !templateCategory) return true;

    return false;
  }, [
    templateName,
    templateType,
    textMessage,
    title,
    description,
    cardOrientation,
    height,
    alignment,
    mediaFile,
    mediaFileError,
    richCardMediaType,
    richCardThumbnailFile,
    richCardThumbnailError,
    carouselCardHeight,
    carouselCardWidth,
    carouselCards,
    carouselCardFiles,
    carouselCardMediaErrors,
    carouselCardThumbnails,
    carouselCardThumbnailErrors,
    templateCategory,
  ]);

  useEffect(() => {
    if (submitTouched && hasValidationError) {
      // Can add an alert or toast here
    }
  }, [submitTouched, hasValidationError]);

  useEffect(() => {
    if (cardOrientation) {
      setAlignmentOptions(alignmentOptionsMap[cardOrientation] || []);
      setAlignment("");
    } else {
      setAlignmentOptions([]);
      setAlignment("");
    }
  }, [cardOrientation]);

  useEffect(() => {
    if (
      alignment &&
      alignmentOptions.length > 0 &&
      !alignmentOptions.find((opt) => opt.value === alignment)
    ) {
      setAlignment("");
    }
  }, [alignmentOptions, alignment]);

  const handleMediaFileChange = async (file) => {
    setMediaFileError("");
    setMediaFile(file);
    if (!file) {
      return;
    }
    await validateMediaFile({
      file,
      type: richCardMediaType,
      orientation: cardOrientation,
      height,
      setError: setMediaFileError,
      context: "rich_card",
      alignment: alignment,
    });
  };

  const handleRichCardThumbnailFileChange = async (file) => {
    setRichCardThumbnailError("");
    setRichCardThumbnailFile(file);
    if (!file) return;
    await validateThumbnailFile({
      file,
      type: "IMAGE",
      orientation: cardOrientation,
      height,
      setError: setRichCardThumbnailError,
      context: "rich_card",
      alignment: alignment,
    });
  };

  const handleCarouselCardFileChange = async (idx, file) => {
    setCarouselCardMediaErrors((prev) => ({ ...prev, [idx]: "" }));
    setCarouselCardFiles((prev) => ({ ...prev, [idx]: file }));
    
    setCarouselCards((prev) =>
      prev.map((card, i) =>
        i === idx
          ? {
              ...card,
              mediaFile: file || undefined,
            }
          : card
      )
    );

    if (!file) return;

    const card = carouselCards[idx];
    const type = card?.mediaType || "IMAGE";
    await validateMediaFile({
      file,
      type,
      orientation: "VERTICAL",
      height: carouselCardHeight,
      width: carouselCardWidth,
      setError: (err) =>
        setCarouselCardMediaErrors((prev) => ({ ...prev, [idx]: err })),
      context: "carousel",
    });
  };

  const handleCarouselCardThumbnailFileChange = async (idx, file) => {
    setCarouselCardThumbnailErrors((prev) => ({ ...prev, [idx]: "" }));
    setCarouselCardThumbnails((prev) => ({ ...prev, [idx]: file }));
    if (!file) return;
    await validateThumbnailFile({
      file,
      type: "IMAGE",
      orientation: "VERTICAL",
      height: carouselCardHeight,
      width: carouselCardWidth,
      setError: (err) =>
        setCarouselCardThumbnailErrors((prev) => ({ ...prev, [idx]: err })),
      context: "carousel",
    });
  };

  const resetSuggestionInput = () => {
    setSuggestionInput({});
    setEditingSuggestionIndex(null);
  };

  const addCarouselCard = () => {
    setCarouselCards((prev) => [
      ...prev,
      {
        title: "",
        description: "",
        mediaType: "IMAGE",
        suggestions: [],
      },
    ]);
    setActiveCarouselCardIndex(carouselCards.length);
    setCarouselCardSuggestionInput({});
    setCarouselCardSuggestionEditingIndex(null);
  };

  const removeCarouselCard = (idx) => {
    setCarouselCards((prev) => prev.filter((_, i) => i !== idx));
    setCarouselCardFiles((prev) => {
      const newFiles = { ...prev };
      delete newFiles[idx];
      return newFiles;
    });
    setCarouselCardThumbnails((prev) => {
      const newThumbs = { ...prev };
      delete newThumbs[idx];
      return newThumbs;
    });
    setCarouselCardMediaErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[idx];
      return newErrors;
    });
    setCarouselCardThumbnailErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[idx];
      return newErrors;
    });
    if (activeCarouselCardIndex === idx) {
      setActiveCarouselCardIndex((prev) => {
        if (carouselCards.length === 1) return null;
        if (idx === 0) return 0;
        return idx - 1;
      });
      setCarouselCardSuggestionInput({});
      setCarouselCardSuggestionEditingIndex(null);
    } else if (activeCarouselCardIndex > idx) {
      setActiveCarouselCardIndex((prev) => prev - 1);
    }
  };

  const editCarouselCard = (idx) => {
    setActiveCarouselCardIndex(idx);
    setCarouselCardSuggestionInput({});
    setCarouselCardSuggestionEditingIndex(null);
  };

  const updateCarouselCard = (idx, updateFn) => {
    setCarouselCards((prev) =>
      prev.map((card, i) =>
        i === idx
          ? typeof updateFn === "function"
            ? updateFn(card)
            : updateFn
          : card
      )
    );
  };

  const validateSuggestion = (sugg) => {
    if (!sugg) return false;
    if (!sugg.suggestionType || !sugg.text || sugg.text.trim() === "")
      return false;

    if (sugg.suggestionType === "url_action") {
      if (
        !sugg.action ||
        !sugg.action.openUrlAction ||
        !sugg.action.openUrlAction.url ||
        sugg.action.openUrlAction.url.trim() === ""
      ) {
        return false;
      }
    }
    if (sugg.suggestionType === "dialer_action") {
      if (
        !sugg.action ||
        !sugg.action.dialAction ||
        !sugg.action.dialAction.phoneNumber ||
        sugg.action.dialAction.phoneNumber.trim() === ""
      ) {
        return false;
      }
    }
    if (sugg.suggestionType === "calendar_event") {
      if (
        !sugg.action ||
        !sugg.action.createCalendarEventAction ||
        !sugg.action.createCalendarEventAction.startTime ||
        !sugg.action.createCalendarEventAction.endTime
      ) {
        return false;
      }
    }
    if (sugg.suggestionType === "view_location_query") {
      if (
        !sugg.action ||
        !sugg.action.viewLocationAction ||
        !sugg.action.viewLocationAction.query ||
        sugg.action.viewLocationAction.query.trim() === ""
      ) {
        return false;
      }
    }
    if (sugg.suggestionType === "view_location_latlong") {
      const latLong = sugg.action?.viewLocationAction?.latLong;
      if (
        !latLong ||
        typeof latLong.latitude !== "number" ||
        typeof latLong.longitude !== "number" ||
        latLong.latitude < -90 ||
        latLong.latitude > 90 ||
        latLong.longitude < -180 ||
        latLong.longitude > 180
      ) {
        return false;
      }
    }
    return true;
  };

  const addCarouselCardSuggestion = () => {
    if (!validateSuggestion(carouselCardSuggestionInput)) return;
    if (activeCarouselCardIndex === null) return;
    if (carouselCardSuggestionEditingIndex !== null) {
      updateCarouselCard(activeCarouselCardIndex, (card) => ({
        ...card,
        suggestions: (card.suggestions || []).map((s, idx) =>
          idx === carouselCardSuggestionEditingIndex
            ? carouselCardSuggestionInput
            : s
        ),
      }));
    } else {
      updateCarouselCard(activeCarouselCardIndex, (card) => ({
        ...card,
        suggestions: [...(card.suggestions || []), carouselCardSuggestionInput],
      }));
    }
    setCarouselCardSuggestionInput({});
    setCarouselCardSuggestionEditingIndex(null);
  };

  const editCarouselCardSuggestion = (idx) => {
    if (activeCarouselCardIndex === null) return;
    const card = carouselCards[activeCarouselCardIndex];
    setCarouselCardSuggestionInput(card.suggestions[idx]);
    setCarouselCardSuggestionEditingIndex(idx);
  };

  const deleteCarouselCardSuggestion = (idx) => {
    if (activeCarouselCardIndex === null) return;
    updateCarouselCard(activeCarouselCardIndex, (card) => ({
      ...card,
      suggestions: (card.suggestions || []).filter((_, i) => i !== idx),
    }));
    if (carouselCardSuggestionEditingIndex === idx) {
      setCarouselCardSuggestionInput({});
      setCarouselCardSuggestionEditingIndex(null);
    }
  };

  const addSuggestion = () => {
    if (!validateSuggestion(suggestionInput)) return;
    if (editingSuggestionIndex !== null) {
      setSuggestions((prev) =>
        prev.map((s, idx) =>
          idx === editingSuggestionIndex ? suggestionInput : s
        )
      );
    } else {
      setSuggestions((prev) => [...prev, suggestionInput]);
    }
    resetSuggestionInput();
  };

  const editSuggestion = (idx) => {
    setSuggestionInput(suggestions[idx]);
    setEditingSuggestionIndex(idx);
  };

  const deleteSuggestion = (idx) => {
    setSuggestions((prev) => prev.filter((_, i) => i !== idx));
    if (editingSuggestionIndex === idx) {
      resetSuggestionInput();
    }
  };

  const buildSuggestion = (sugg) => {
    const base = {
      text: sugg.text,
      suggestionType: sugg.suggestionType,
      postbackData:
        sugg.postbackData || provider === KARIX ? "message_id" : ["message_id"],
    };
    switch (sugg.suggestionType) {
      case "url_action":
        return {
          action: {
            ...base,
            openUrlAction: {
              url: sugg.action?.openUrlAction?.url || "",
            },
            application: sugg.action?.application || "",
          },
        };
      case "dialer_action":
        return {
          action: {
            ...base,
            dialAction: {
              phoneNumber: sugg.action?.dialAction?.phoneNumber || "",
            },
          },
        };
      case "calendar_event":
        return {
          action: {
            ...base,
            createCalendarEventAction: {
              startTime: formatDateTimeToYMDHMS(
                sugg.action?.createCalendarEventAction?.startTime || ""
              ),
              endTime: formatDateTimeToYMDHMS(
                sugg.action?.createCalendarEventAction?.endTime || ""
              ),
              title: sugg.action?.createCalendarEventAction?.title || "",
              description:
                sugg.action?.createCalendarEventAction?.description || "",
            },
          },
        };
      case "view_location_query":
        return {
          action: {
            ...base,
            viewLocationAction: {
              query: sugg.action?.viewLocationAction?.query || "",
            },
          },
        };
      case "view_location_latlong":
        return {
          action: {
            ...base,
            viewLocationAction: {
              latLong: {
                latitude:
                  sugg.action?.viewLocationAction?.latLong?.latitude || 0,
                longitude:
                  sugg.action?.viewLocationAction?.latLong?.longitude || 0,
              },
              label: sugg.action?.viewLocationAction?.label || "",
            },
          },
        };
      default:
        return { action: base };
    }
  };

  const resetCommonFields = () => {
    setTextMessage("");
    setCardOrientation("");
    setAlignmentOptions([]);
    setAlignment("");
    setHeight("");
    setTitle("");
    setDescription("");
    setMediaFile(null);
    setMediaFileError("");
    setRichCardThumbnailFile(null);
    setRichCardThumbnailError("");
    setSuggestionInput({});
    setSuggestions([]);
    setEditingSuggestionIndex(null);
    setCarouselCards([]);
    setCarouselCardFiles({});
    setCarouselCardThumbnails({});
    setCarouselCardMediaErrors({});
    setCarouselCardThumbnailErrors({});
    setActiveCarouselCardIndex(null);
    setCarouselCardSuggestionInput({});
    setCarouselCardSuggestionEditingIndex(null);
    setCarouselCardHeight("");
    setCarouselCardWidth("");
    setTemplateCategory("TRANSACTIONAL");
  };

  const handleTemplateTypeChange = (opt) => {
    setTemplateType(opt?.value || "");
    resetCommonFields();
    if (opt?.value === "carousel") {
      setCarouselCards([
        {
          title: "",
          description: "",
          mediaType: "IMAGE",
          suggestions: [],
        },
        {
          title: "",
          description: "",
          mediaType: "IMAGE",
          suggestions: [],
        },
      ]);
      setActiveCarouselCardIndex(0);
    }
    if (opt?.value === "rich_card") {
      setCardOrientation("");
      setAlignmentOptions([]);
      setAlignment("");
    }
  };

  const resetAllFields = () => {
    setTemplateName("");
    setTemplateType("");
    setTemplateNameError("");
    resetCommonFields();
  };

  const getAllVariablesForKarix = () => {
    let variables = [];
    if (templateType === "text_message" || templateType === "standard") {
      variables = extractVariablesFromText(textMessage);
    } else if (templateType === "rich_card") {
      variables = [
        ...extractVariablesFromText(title),
        ...extractVariablesFromText(description),
      ];
    } else if (templateType === "carousel") {
      variables = extractVariablesFromCarouselCards(carouselCards);
    }
    return Array.from(new Set(variables));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitTouched(true);

    const nameError = validateTemplateName(templateName);
    setTemplateNameError(nameError);
    if (nameError) return;

    if (hasValidationError) return;

    if (templateType === "rich_card" && mediaFile) {
      const valid = await validateMediaFile({
        file: mediaFile,
        type: richCardMediaType,
        orientation: cardOrientation,
        alignment: alignment,
        height,
        setError: setMediaFileError,
        context: "rich_card",
      });
      if (!valid) return;
    }

    if (
      templateType === "rich_card" &&
      richCardMediaType === "VIDEO" &&
      richCardThumbnailFile
    ) {
      const valid = await validateThumbnailFile({
        file: richCardThumbnailFile,
        type: "IMAGE",
        orientation: cardOrientation,
        height,
        setError: setRichCardThumbnailError,
        context: "rich_card",
        alignment: alignment,
      });
      if (!valid) return;
    }

    if (templateType === "carousel") {
      let allValid = true;
      for (let idx = 0; idx < carouselCards.length; idx++) {
        const card = carouselCards[idx];
        if (carouselCardFiles[idx]) {
          const valid = await validateMediaFile({
            file: carouselCardFiles[idx],
            type: card.mediaType || "IMAGE",
            orientation: "VERTICAL",
            height: carouselCardHeight,
            width: carouselCardWidth,
            setError: (err) =>
              setCarouselCardMediaErrors((prev) => ({
                ...prev,
                [idx]: err,
              })),
            context: "carousel",
          });
          if (!valid) {
            allValid = false;
          }
        }
        if (card.mediaType === "VIDEO" && carouselCardThumbnails[idx]) {
          const validThumb = await validateThumbnailFile({
            file: carouselCardThumbnails[idx],
            type: "IMAGE",
            orientation: "VERTICAL",
            height: carouselCardHeight,
            width: carouselCardWidth,
            setError: (err) =>
              setCarouselCardThumbnailErrors((prev) => ({
                ...prev,
                [idx]: err,
              })),
            context: "carousel",
          });
          if (!validThumb) {
            allValid = false;
          }
        }
      }
      if (!allValid) return;
    }

    const formData = new FormData();

    let karixTemplatePlaceHolderCount = 0;
    let karixTemplateParamNames = [];
    if (provider === KARIX) {
      const vars = getAllVariablesForKarix();
      karixTemplatePlaceHolderCount = vars.length;
      karixTemplateParamNames = vars;
    }

    let payload = {
      templateName,
      templateType,
      message: {},
      options:
        provider === SMART_PING
          ? { provider: SMART_PING }
          : {
            provider: KARIX,
            karixOptions: {
              templatePlaceHolderCount: karixTemplatePlaceHolderCount,
              templateParamNames: karixTemplateParamNames,
              templateCategory: templateCategory || "TRANSACTIONAL",
            },
          },
    };
    if (templateType === "text_message" || templateType === "standard") {
      payload.message.text = textMessage;
      payload.message.suggestions = suggestions.map(buildSuggestion);
    }
    if (templateType === "rich_card") {
      let cardContent = {
        title,
        description,
        fileName: mediaFile?.name || "file.png",
        media: {
          fileType: richCardMediaType || "IMAGE",
          contentInfo: {},
        },
        suggestions: suggestions.map(buildSuggestion),
      };
      if (cardOrientation === "VERTICAL") {
        cardContent.media.height = height;
      }
      if (cardOrientation === "HORIZONTAL") {
        cardContent.alignment = alignment;
      }
      if (richCardMediaType === "VIDEO" && richCardThumbnailFile) {
        cardContent.thumbnailFileName = richCardThumbnailFile.name;
      }
      payload.message = {
        standaloneCard: {
          cardOrientation,
          cardContent,
        },
      };
      if (mediaFile) {
        formData.append("file", mediaFile);
      }
      if (richCardMediaType === "VIDEO" && richCardThumbnailFile) {
        formData.append("thumbnailFile", richCardThumbnailFile);
      }
    }
    if (templateType === "carousel") {
      payload.message = {
        carouselCard: {
          cardHeight: carouselCardHeight,
          cardWidth: carouselCardWidth,
          cardContents: carouselCards.map((card, idx) => ({
            title: card.title,
            description: card.description,
            fileName: carouselCardFiles[idx]?.name || "media.png",
            ...(card.mediaType === "VIDEO" && carouselCardThumbnails[idx]
              ? { thumbnailFileName: carouselCardThumbnails[idx].name }
              : {}),
            media: {
              fileType: card.mediaType || "IMAGE",
              contentInfo: {},
            },
            suggestions: (card.suggestions || []).map(buildSuggestion),
          })),
        },
      };
      carouselCards.forEach((card, idx) => {
        if (carouselCardFiles[idx]) {
          formData.append(`files[${idx}]`, carouselCardFiles[idx]);
        }
        if (card.mediaType === "VIDEO" && carouselCardThumbnails[idx]) {
          formData.append(`thumbnails[${idx}]`, carouselCardThumbnails[idx]);
        }
      });
    }
    Object.entries(payload).forEach(([key, value]) => {
      formData.append(
        key,
        typeof value === "object" ? JSON.stringify(value) : value
      );
    });

    setShowModal(true);
    try {
      const response = await registerRCSTemplate(formData).unwrap();
      if (response?.statusCode === 200) {
        setReason(response?.data?.message);
        setVariant('tertiary');
        setProgress(100);
        setSubmitTouched(false);
      } else if (response?.data?.statusCode > 300) {
        setReason(response?.data?.message || 'Upload failed!');
        setVariant('warning');
        setProgress(100);
    }
    } catch (err) {
      setReason(err?.message || 'Upload failed');
      setVariant('warning');
      setShowModal(true);
      setProgress(100);
    }
  };

  const AddVariableButton = ({ value, setValue, inputRef, prefix }) => {
    return (
      <Button
        onClick={() => {
          const nextVar = getNextVariableName(value, prefix);
          insertAtCursor(inputRef, value, setValue, nextVar);
        }}
        bsPrefix="text"
        style={{
          border: "none",
          background: "none",
          padding: 0,
          color: "inherit",
        }}
        variant="secondary"
        className="d-flex align-items-center text-success fw-bold"
        type="button"
      >
        <FcPlus className="icon icon-xs me-2" />
        Add Variable
      </Button>
    );
  };

  const richCardPreview = useMemo(
    () => (mediaFile ? URL.createObjectURL(mediaFile) : null),
    [mediaFile]
  );
  const richCardThumbnailPreview = useMemo(
    () =>
      richCardThumbnailFile ? URL.createObjectURL(richCardThumbnailFile) : null,
    [richCardThumbnailFile]
  );

  useEffect(() => {
    return () => {
      if (richCardPreview) URL.revokeObjectURL(richCardPreview);
      if (richCardThumbnailPreview)
        URL.revokeObjectURL(richCardThumbnailPreview);
    };
  }, [richCardPreview, richCardThumbnailPreview]);

  const isRichCardMediaDisabled =
    !cardOrientation ||
    (cardOrientation === "VERTICAL" && !height) ||
    (cardOrientation === "HORIZONTAL" && !alignment);

  const richCardMediaAccept = useMemo(() => {
    if (richCardMediaType === "IMAGE") return "image/*";
    if (richCardMediaType === "VIDEO") return "video/*";
    return "image/*,video/*";
  }, [richCardMediaType]);
  const richCardThumbnailAccept = "image/*";

  const getCarouselCardMediaAccept = (mediaType) => {
    if (mediaType === "IMAGE") return "image/*";
    if (mediaType === "VIDEO") return "video/*";
    return "image/*,video/*";
  };
  const carouselCardThumbnailAccept = "image/*";


  const handleCloseModal = () => {
    setShowModal(false);
    setTimeout(() => {
        setProgress(0);
    }, 100);
    setReason('');
    setVariant('primary');
};
const handleRefresh = () => {
  resetAllFields();
  setSubmitTouched(false);
  setShowModal(false);
  setTimeout(() => {
      setProgress(0);
  }, 100);
  history.push({
    pathname: Routes.Templates.path,
    state: { tab: "RCS" },
  });
  setReason('');
  setVariant('primary');
};
useEffect(() => {
  if (showModal) {
      const interval = setInterval(() => {
          setProgress(prevProgress => {
              if (prevProgress >= 100) {
                  clearInterval(interval);
                  return 100;
              }
              return Math.min(prevProgress + 5, 95);
          });
      }, 100);
  }
}, [showModal]);

  return (
    <>
      <div className="d-flex flex-row-reverse gap-3">
        <RcsTemplatePreview
          templateType={templateType}
          textMessage={textMessage}
          suggestions={suggestions}
          mediaFile={mediaFile}
          title={title}
          description={description}
          carouselCards={carouselCards}
        />
        <Form
          onSubmit={handleSubmit}
          autoComplete="off"
          encType="multipart/form-data"
          className="w-100"
        >
          <div className="mid-section-scroll">
            <div className="card p-3 m-0">
              <Row>
                <Form.Group as={Col} lg={6}>
                  <Form.Label htmlFor="templateName">
                    Template Name <span className="text-danger"> * </span>
                  </Form.Label>
                  <Form.Control
                    id="templateName"
                    name="templateName"
                    required
                    type="text"
                    value={templateName}
                    onChange={(e) => {
                      setTemplateName(e.target.value);
                      setTemplateNameError(validateTemplateName(e.target.value));
                    }}
                    placeholder="Enter Template Name"
                    maxLength={20}
                    autoComplete="off"
                    isInvalid={!!templateNameError && (submitTouched || templateName.length > 0)}
                    title="Allowed: letters, numbers, underscores, hyphens. Max 20 characters."
                  />
                  {templateNameError && (submitTouched || templateName.length > 0) && (
                    <div className="invalid-feedback d-block">
                      {templateNameError}
                    </div>
                  )}
                </Form.Group>
                <Form.Group as={Col} lg={6}>
                  <Form.Label htmlFor="templateType">
                    Template Type <span className="text-danger"> * </span>
                  </Form.Label>
                  <Select
                    inputId="templateType"
                    name="templateType"
                    options={templateTypeOptions}
                    placeholder={"Select Template Type"}
                    className="mb-0"
                    value={templateTypeOptions.find(
                      (opt) => opt.value === templateType
                    )}
                    onChange={handleTemplateTypeChange}
                  />
                </Form.Group>
              </Row>
              {provider === KARIX && (
                <Row className="mt-3">
                  <Form.Group as={Col} lg={6}>
                    <Form.Label htmlFor="templateCategory">
                      Template Category <span className="text-danger"> * </span>
                    </Form.Label>
                    <Select
                      inputId="templateCategory"
                      name="templateCategory"
                      options={templateCategoryOptions}
                      value={templateCategoryOptions.find(
                        (opt) => opt.value === templateCategory
                      )}
                      onChange={(opt) =>
                        setTemplateCategory(opt?.value || "TRANSACTIONAL")
                      }
                      placeholder="Select Template Category"
                    />
                  </Form.Group>
                </Row>
              )}
            </div>

            {(templateType === "text_message" ||
              templateType === "standard") && (
                <div className="card p-3 m-0 mt-3">
                  <Row>
                    <Form.Group as={Col} className="mb-3 withChars">
                      <div className="d-flex justify-content-between">
                        <Form.Label htmlFor="textMessage">
                          Text Message <span className="text-danger"> * </span>
                        </Form.Label>
                        <AddVariableButton
                          value={textMessage}
                          setValue={setTextMessage}
                          inputRef={textMessageRef}
                          prefix="custom_param_text_"
                        />
                      </div>
                      <Form.Control
                        id="textMessage"
                        name="textMessage"
                        required
                        as="textarea"
                        rows={5}
                        value={textMessage}
                        onChange={(e) =>
                          setTextMessage(e.target.value.slice(0, 100))
                        }
                        maxLength={100}
                        ref={textMessageRef}
                      />
                      <p className="chars_count">
                        {typeof textMessage === "string" ? textMessage.length : 0}
                        /100
                      </p>
                      <Form.Control.Feedback type="invalid">
                        Please provide the content of the standard text message.
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>

                  <h5 className="mt-4 mb-3">Suggestions</h5>
                  <RcsSuggestionsField
                    suggestion={suggestionInput}
                    setSuggestion={setSuggestionInput}
                    addSuggestion={addSuggestion}
                    suggestions={suggestions}
                    onEdit={editSuggestion}
                    onDelete={deleteSuggestion}
                    editingIndex={editingSuggestionIndex}
                    setEditingIndex={setEditingSuggestionIndex}
                  />
                </div>
              )}

            {templateType === "rich_card" && (
              <div className="card p-3 m-0 mt-3">
                <Row className="mb-3">
                  <Form.Group
                    as={Col}
                    lg={cardOrientation === "HORIZONTAL" ? 6 : 12}
                  >
                    <Form.Label htmlFor="cardOrientation">
                      Card Orientation <span className="text-danger"> * </span>
                    </Form.Label>
                    <Select
                      inputId="cardOrientation"
                      name="cardOrientation"
                      options={cardOrientationOptions}
                      value={cardOrientationOptions.find(
                        (opt) => opt.value === cardOrientation
                      )}
                      onChange={(opt) => setCardOrientation(opt?.value || "")}
                    />
                  </Form.Group>
                  {cardOrientation === "HORIZONTAL" && (
                    <Form.Group as={Col} lg={6}>
                      <Form.Label htmlFor="cardAlignment">
                        Card Alignment <span className="text-danger"> * </span>
                      </Form.Label>
                      <Select
                        inputId="cardAlignment"
                        name="cardAlignment"
                        options={alignmentOptions}
                        value={alignmentOptions.find(
                          (opt) => opt.value === alignment
                        )}
                        onChange={(opt) => setAlignment(opt?.value || "")}
                        isDisabled={alignmentOptions.length === 0}
                      />
                    </Form.Group>
                  )}
                </Row>

                <Form.Group className="mt-3">
                  <div className="d-flex justify-content-between">
                    <Form.Label htmlFor="title">
                      Title <span className="text-danger"> * </span>
                    </Form.Label>
                    <AddVariableButton
                      value={title}
                      setValue={setTitle}
                      inputRef={titleRef}
                      prefix="custom_param_title_"
                    />
                  </div>
                  <Form.Control
                    id="title"
                    name="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value.slice(0, 100))}
                    maxLength={100}
                    ref={titleRef}
                  />
                </Form.Group>

                <Form.Group className="mt-3">
                  <div className="d-flex justify-content-between">
                    <Form.Label htmlFor="description">
                      Description <span className="text-danger"> * </span>
                    </Form.Label>
                    <AddVariableButton
                      value={description}
                      setValue={setDescription}
                      inputRef={descriptionRef}
                      prefix="custom_param_desc_"
                    />
                  </div>
                  <Form.Control
                    id="description"
                    name="description"
                    as="textarea"
                    rows={4}
                    value={description}
                    onChange={(e) =>
                      setDescription(e.target.value.slice(0, 500))
                    }
                    maxLength={500}
                    ref={descriptionRef}
                  />
                </Form.Group>
                {cardOrientation === "VERTICAL" && (
                  <Row className="mt-3">
                    <Form.Group as={Col} lg={12}>
                      <Form.Label htmlFor="cardHeight">
                        Card Height <span className="text-danger"> * </span>
                      </Form.Label>
                      <Select
                        inputId="cardHeight"
                        name="cardHeight"
                        options={heightOptions}
                        value={heightOptions.find(
                          (opt) => opt.value === height
                        )}
                        onChange={(opt) => setHeight(opt?.value || "")}
                      />
                    </Form.Group>
                  </Row>
                )}
                <Form.Group className="mt-3">
                  <Form.Label>
                    Header <span className="text-danger"> * </span>
                  </Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      id={`richCardMediaType-image`}
                      label="Image"
                      name={`richCardMediaType`}
                      value="IMAGE"
                      checked={richCardMediaType === "IMAGE"}
                      onChange={() => setRichCardMediaType("IMAGE")}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id={`richCardMediaType-video`}
                      label="Video"
                      name={`richCardMediaType`}
                      value="VIDEO"
                      checked={richCardMediaType === "VIDEO"}
                      onChange={() => setRichCardMediaType("VIDEO")}
                    />
                  </div>
                </Form.Group>
                <Row className="mt-3">
                  <Form.Group as={Col} lg={12}>
                    <Form.Label htmlFor="mediaFile">
                      Upload Media <span className="text-danger"> * </span>
                    </Form.Label>
                    <RcsDropMediaAndPreview
                      file={mediaFile}
                      preview={richCardPreview}
                      onFileChange={handleMediaFileChange}
                      inputId="mediaFile"
                      accept={richCardMediaAccept}
                      disabled={isRichCardMediaDisabled}
                    />
                    {isRichCardMediaDisabled && (
                      <div className="text-warning small mt-1">
                        Please select card orientation and{" "}
                        {cardOrientation === "VERTICAL"
                          ? "height"
                          : cardOrientation === "HORIZONTAL"
                            ? "alignment"
                            : "required options"}{" "}
                        before uploading media.
                      </div>
                    )}
                    {mediaFileError && (
                      <Alert variant="danger" className="mt-2 mb-0 py-1 px-2">
                        {mediaFileError}
                      </Alert>
                    )}
                  </Form.Group>
                </Row>
                {richCardMediaType === "VIDEO" && (
                  <Row className="mt-3">
                    <Form.Group as={Col} lg={12}>
                      <Form.Label htmlFor="richCardThumbnailFile">
                        Upload Thumbnail{" "}
                        <span className="text-danger"> * </span>
                      </Form.Label>
                      <RcsDropMediaAndPreview
                        file={richCardThumbnailFile}
                        preview={richCardThumbnailPreview}
                        onFileChange={handleRichCardThumbnailFileChange}
                        inputId="richCardThumbnailFile"
                        accept={richCardThumbnailAccept}
                        disabled={isRichCardMediaDisabled}
                      />
                      {isRichCardMediaDisabled && (
                        <div className="text-warning small mt-1">
                          Please select card orientation and{" "}
                          {cardOrientation === "VERTICAL"
                            ? "height"
                            : cardOrientation === "HORIZONTAL"
                              ? "alignment"
                              : "required options"}{" "}
                          before uploading thumbnail.
                        </div>
                      )}
                      {richCardThumbnailError && (
                        <Alert variant="danger" className="mt-2 mb-0 py-1 px-2">
                          {richCardThumbnailError}
                        </Alert>
                      )}
                    </Form.Group>
                  </Row>
                )}

                <h5 className="mt-4">Suggestions</h5>
                <RcsSuggestionsField
                  suggestion={suggestionInput}
                  setSuggestion={setSuggestionInput}
                  addSuggestion={addSuggestion}
                  suggestions={suggestions}
                  onEdit={editSuggestion}
                  onDelete={deleteSuggestion}
                  editingIndex={editingSuggestionIndex}
                  setEditingIndex={setEditingSuggestionIndex}
                />
              </div>
            )}

            {templateType === "carousel" && (
              <div className="card p-3 m-0 mt-3">
                <Row className="mb-3">
                  <Form.Group as={Col} lg={6}>
                    <Form.Label htmlFor="carouselCardHeight">
                      Card Height <span className="text-danger"> * </span>
                    </Form.Label>
                    <Select
                      inputId="carouselCardHeight"
                      name="carouselCardHeight"
                      options={heightOptions}
                      value={heightOptions.find(
                        (opt) => opt.value === carouselCardHeight
                      )}
                      onChange={(opt) =>
                        setCarouselCardHeight(opt?.value || "")
                      }
                    />
                  </Form.Group>
                  <Form.Group as={Col} lg={6}>
                    <Form.Label htmlFor="carouselCardWidth">
                      Card Width <span className="text-danger"> * </span>
                    </Form.Label>
                    <Select
                      inputId="carouselCardWidth"
                      name="carouselCardWidth"
                      options={[
                        { value: "SMALL", label: "Small" },
                        { value: "MEDIUM", label: "Medium" },
                      ]}
                      value={[
                        { value: "SMALL", label: "Small" },
                        { value: "MEDIUM", label: "Medium" },
                      ].find((opt) => opt.value === carouselCardWidth)}
                      onChange={(opt) => setCarouselCardWidth(opt?.value || "")}
                    />
                  </Form.Group>
                </Row>
                <div className="d-flex justify-content-end">
                  <Button
                    variant="primary"
                    onClick={(e) => {
                      e.preventDefault();
                      addCarouselCard();
                    }}
                    type="button"
                    disabled={
                      activeCarouselCardIndex !== null &&
                      activeCarouselCardIndex === carouselCards.length
                    }
                  >
                    Add Card to Carousel
                  </Button>
                </div>
                {carouselCards.length > 0 && (
                  <div className="">
                    <h5>Cards in Carousel</h5>
                    <div className="card light m-0">
                      <ul className="list-group">
                        {carouselCards.map((card, idx) => (
                          <li
                            key={idx}
                            className={`list-group-item border rounded-2 mb-2 d-flex justify-content-between align-items-center  ${activeCarouselCardIndex === idx ? "active" : ""
                              }`}
                            style={
                              activeCarouselCardIndex === idx
                                ? { background: "rgb(38 43 64)" }
                                : {}
                            }
                          >
                            <span>
                              <strong>{card.title || "Untitled Card"}</strong>
                              {card.description
                                ? ` - ${card.description.slice(0, 30)}${card.description.length > 30 ? "..." : ""
                                }`
                                : ""}
                            </span>
                            <span className="py-2 d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-1"
                                onClick={() => editCarouselCard(idx)}
                                type="button"
                                disabled={activeCarouselCardIndex === idx}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => removeCarouselCard(idx)}
                                type="button"
                              >
                                Remove
                              </Button>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {activeCarouselCardIndex !== null &&
                  carouselCards[activeCarouselCardIndex] && (
                    <RcsCarouselCardForm
                      card={carouselCards[activeCarouselCardIndex]}
                      setCard={(updateFn) => {
                        if (typeof updateFn === "function") {
                          updateCarouselCard(activeCarouselCardIndex, updateFn);
                        } else {
                          updateCarouselCard(
                            activeCarouselCardIndex,
                            () => updateFn
                          );
                        }
                      }}
                      idPrefix={`carouselCard_${activeCarouselCardIndex}_`}
                      suggestionInput={carouselCardSuggestionInput}
                      setSuggestionInput={setCarouselCardSuggestionInput}
                      addSuggestion={addCarouselCardSuggestion}
                      editingSuggestionIndex={
                        carouselCardSuggestionEditingIndex
                      }
                      setEditingSuggestionIndex={
                        setCarouselCardSuggestionEditingIndex
                      }
                      onEditSuggestion={editCarouselCardSuggestion}
                      onDeleteSuggestion={deleteCarouselCardSuggestion}
                      file={carouselCardFiles[activeCarouselCardIndex]}
                      onFileChange={(file) =>
                        handleCarouselCardFileChange(
                          activeCarouselCardIndex,
                          file
                        )
                      }
                      mediaError={
                        carouselCardMediaErrors[activeCarouselCardIndex] || ""
                      }
                      thumbnailFile={
                        carouselCardThumbnails[activeCarouselCardIndex]
                      }
                      onThumbnailFileChange={(file) =>
                        handleCarouselCardThumbnailFileChange(
                          activeCarouselCardIndex,
                          file
                        )
                      }
                      thumbnailError={
                        carouselCardThumbnailErrors[activeCarouselCardIndex] ||
                        ""
                      }
                      carouselCards={carouselCards}
                      cardIndex={activeCarouselCardIndex}
                      carouselCardHeight={carouselCardHeight}
                      carouselCardWidth={carouselCardWidth}
                      mediaAccept={getCarouselCardMediaAccept(
                        carouselCards[activeCarouselCardIndex]?.mediaType
                      )}
                      thumbnailAccept={carouselCardThumbnailAccept}
                    />
                  )}
              </div>
            )}

            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="primary"
                type="submit"
                className="mt-4"
                name="submitTemplate"
                disabled={hasValidationError}
              >
                Submit Template
              </Button>
            </div>
          </div>
        </Form>
      </div>


      <Modal
        centered
        show={showModal}
        onHide={handleCloseModal}
        className={`modal-${variant}`}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="mx-auto">
          <p className="lead mb-0 text-white">Creating SMS Template</p>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <div className="py-3 px-5 text-center">
            {progress !== 100 ?
              (<span className="modal-icon display-1">
                <ClockIcon className="icon icon-xl text-white" />
              </span>) :
              variant === 'tertiary' ?
                (<span className="modal-icon display-1">
                  <CheckCircleIcon className="icon icon-xl text-white" />
                </span>) :
                <span className="modal-icon display-1">
                  <ShieldExclamationIcon className="icon icon-xl text-white" />
                </span>
            }

            <Modal.Title className="text-white mb-3">
              {progress !== 100 ? <h3>Sending Request</h3> : variant === 'tertiary' ? <h3>Template Created Successfully</h3> : <h3>Something Went Wrong</h3>}
            </Modal.Title>
            <ProgressBar variant="secondary" now={progress} min={0} max={100} animated />
            {variant !== 'tertiary' ?
              <p className="mb-4 text-danger">
                {reason}
              </p> : ''}
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-center pt-0 pb-3">
          <Button variant="white" size="sm" className="text-primary fw-bold" onClick={handleRefresh}>
            OK
          </Button>
          {variant !== 'tertiary' && <Button variant="white" size="sm" className="text-primary fw-bold" onClick={handleCloseModal}>
            Retry
          </Button>}
        </Modal.Footer>
      </Modal>
    </>
  );
};
